import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { useVeriff } from '../../../Hooks/useVeriff';

export interface KaziCardProps {
  title: string;
  titleVariant?: 'lora-title-large' | 'lora-title-medium' | 'lora-title-small';
  progress?: number;
  subtitle?: string;
  list?: ReactNode;
  subtitle2?: string;
  subtitle3?: string;
  icon?: any;
  badge?: string | ReactNode;
  body?: string;
  body2?: string;
  label?: string;
  tags?: string[];
  cardAction?: any;
  isLoading?: boolean;
  veriff?: boolean;
  button?: {
    label: string;
    action: (props: any) => any;
    disabled?: boolean;
    variant?: 'outlined' | 'contained' | 'text';
    sx?: SxProps;
  };
  secondaryButton?: {
    label: string;
    action: (props: any) => any;
    disabled?: boolean;
    variant?: 'outlined' | 'contained' | 'text';
    sx?: SxProps;
  };
  showDivider?: boolean;
  sx?: SxProps;
}

export const KaziCard = (props: KaziCardProps) => {
  // TODO: This compoenent is over engineered and can be simplified or split out
  /* ******************** Hooks ******************** */
  const theme = useTheme();
  const { mountVeriff } = useVeriff();
  /* ******************** Variables ******************** */
  const { skills, hiddenSkillCount } = useMemo(() => {
    let skills: string[] = [];
    let hiddenSkillCount = 0;

    if (props.tags) {
      skills = props.tags;
      if (props.tags.length > 3) {
        skills = props.tags.slice(0, 3);
        hiddenSkillCount = props.tags.length - 3;
      }
    }

    return { skills, hiddenSkillCount };
  }, []);
  /* ******************** Functions ******************** */
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  return (
    <Card
      className="KaziJobCard"
      sx={{
        minWidth: '259px',
        borderRadius: '18px',
        height: 'fit-content',
        maxHeight: '465px',
        border: `1px solid ${theme.palette.background.secondary}`,
        boxShadow: 'none',
        '.MuiCardActionArea-focusHighlight': {
          background: !props.cardAction ? 'transparent' : '',
        },
        ...props?.sx,
      }}
    >
      <CardActionArea
        sx={{
          cursor: !props.cardAction ? 'auto' : 'pointer',
        }}
        onClick={props?.cardAction}
      >
        {props?.icon &&
          (props?.isLoading ? (
            <Skeleton
              variant="circular"
              sx={{ fontSize: '24px', width: '28px', margin: '16px' }}
            />
          ) : (
            <CardHeader
              avatar={props.icon}
              action={
                props.badge &&
                (typeof props.badge === 'string' ? (
                  <Chip sx={{ marginRight: 1 }} label={props.badge} />
                ) : (
                  props.badge
                ))
              }
              sx={{ paddingBottom: 0 }}
            />
          ))}
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingTop: props?.icon ? 1 : 2,
          }}
        >
          {props?.isLoading ? (
            <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
          ) : (
            <Typography
              variant={props?.titleVariant ?? 'lora-title-large'}
              marginBottom={1}
              fontWeight={'bold'}
            >
              {props.title}
            </Typography>
          )}
          {typeof props.progress === 'number' &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Box>
                <Typography variant="body-small" color={'black.200'}>
                  {props.progress}% complete
                </Typography>
                <Box height={'fit-content !important'} marginY={1}>
                  <LinearProgress
                    variant="determinate"
                    value={props.progress}
                  />
                </Box>
              </Box>
            ))}
          {props?.subtitle &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Typography
                variant="body-medium"
                marginBottom={1}
                color={'black.400'}
              >
                {props.subtitle}
              </Typography>
            ))}
          {props?.list && !props?.isLoading && props.list}
          {props?.subtitle2 &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Typography
                variant="body-medium"
                marginBottom={1}
                color={'black.300'}
              >
                {props.subtitle2}
              </Typography>
            ))}
          {props?.body &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Typography
                variant="body-small"
                marginBottom={1}
                color={'black.200'}
              >
                {props.body}
              </Typography>
            ))}
          {props?.showDivider && <Divider sx={{ marginBottom: 1 }} />}
          {props?.body2 &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Typography
                variant="body-small"
                marginBottom={1}
                color={'black.200'}
              >
                {props.body2}
              </Typography>
            ))}
          {props?.subtitle3 &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Typography
                variant="body-medium"
                marginBottom={1}
                color={'black.200'}
              >
                {props.subtitle3}
              </Typography>
            ))}
          {skills.length > 0 &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Grid
                gap={1}
                display={'flex'}
                flexDirection={'row'}
                marginBottom={1.5}
                overflow={'auto'}
                sx={{
                  '&.MuiGrid-root::-webkit-scrollbar': { display: 'none' },
                  '&.MuiGrid-root': {
                    '-ms-overflow-style': 'none',
                    'scorllbar-width': 'none',
                  },
                }}
              >
                {skills.map((tag, tagIdx) => (
                  <Grid key={`${tag}-${props.title}-${tagIdx}`} xs="auto">
                    <Chip label={tag} />
                  </Grid>
                ))}
                <Grid
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                >
                  <Typography>
                    {hiddenSkillCount > 0 && `+${hiddenSkillCount}`}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          {props?.label &&
            (props?.isLoading ? (
              <Skeleton variant="rectangular" sx={{ marginBottom: 2 }} />
            ) : (
              <Typography variant="label-small" color={'black.200'}>
                {props.label}
              </Typography>
            ))}
        </CardContent>
      </CardActionArea>
      {(props?.button || props?.secondaryButton || props?.veriff) && (
        <CardActions>
          {props?.veriff && <div id="veriff-root"></div>}
          {props?.secondaryButton &&
            (props?.isLoading ? (
              <Skeleton
                variant="rounded"
                sx={{
                  fontSize: '37px',
                  width: '95px',
                  marginLeft: 'auto',
                  marginBottom: 1,
                }}
              />
            ) : (
              <Button
                variant={props?.secondaryButton?.variant ?? 'outlined'}
                sx={{
                  marginLeft: 'auto',
                  marginBottom: 1,
                  ...props?.secondaryButton?.sx,
                }}
                onClick={props.secondaryButton.action}
                disabled={props?.secondaryButton.disabled ?? false}
              >
                {props.secondaryButton.label}
              </Button>
            ))}
          {props?.button &&
            (props?.isLoading ? (
              <Skeleton
                variant="rounded"
                sx={{
                  fontSize: '37px',
                  width: '95px',
                  marginLeft: 'auto',
                  marginBottom: 1,
                }}
              />
            ) : (
              <Button
                variant={props?.button?.variant ?? 'contained'}
                sx={{
                  marginLeft: 'auto',
                  marginBottom: 1,
                  ...props?.button?.sx,
                }}
                onClick={props.button.action}
                disabled={props?.button.disabled ?? false}
              >
                {props.button.label}
              </Button>
            ))}
        </CardActions>
      )}
    </Card>
  );
};
